import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import MyAppText from '../../components/MyAppText';
import { translate } from '../../services/translate';
import moment from 'moment';
import MyDropDownPicker from '../../components/MyDropDownPicker';
import { cameraService, GetCamerasParameters } from '../../services/central-api/cameras';
import { accessService } from '../../services/central-api/access';
import MyDataTable from '../../components/MyDataTable';
import { WindowInformation } from '../../services/window-information';
import { dashboardSummaryService, GetCamerasSummary } from '../../services/central-api/dashboard-summary';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import DropdownButton, { Action } from '../../components/DropdownButton';
import { useAuth } from '../../context/Auth';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import DataTableContainer from '../../components/DataTableContainer';
import Container from '../../components/Container';
import Header from '../../components/Header';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { CameraType } from '../../schemas/cameras';
import { RootNavigation } from '../../Routes';
import { openInNewTab } from '../../services/navigation-assistant';

export default function DashboardCameras({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();
    const { styles, theme } = useStyles(styleSheet);

    const [summary, setSummary] = useState<GetCamerasSummary>({
        lprOnline: 0,
        lprOffline: 0,
        pinnedOnline: 0,
        pinnedOffline: 0,
        ptzOnline: 0,
        ptzOffline: 0,
        online: 0,
        offline: 0,
        stolen: 0,
        createdToday: 0,
        panoramicOnline: 0,
        panoramicOffline: 0,
        groupedCoordinatedCount: 0,
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [cameras, setCameras] = useState<DashboardCamera[]>([]);

    // Camera type picker
    const [isCameraTypePickerOpen, setIsCameraTypePickerOpen] = useState<boolean>(false);
    const [selectedCameraTypeFilter, setSelectedCameraTypeFilter] = useState<CameraType | 'all'>('all');
    const [selectableCamerasTypeFilter, setSelectableCamerasTypeFilter] = useState<{ label: string; value: CameraType | 'all'; }[]>([]);

    // Installation Company picker
    const [isInstallationCompanyPickerOpen, setIsInstallationCompanyPickerOpen] = useState<boolean>(false);
    const [selectableInstallationCompanyFilter, setSelectableInstallationCompanyFilter] = useState<{ label: string; value: number; }[]>([]);
    const [selectedInstallationCompanyFilter, setSelectedInstallationCompanyFilter] = useState<number>(0);

    // Date picker
    const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
    const [selectableDateFilter, setSelectableDateFilter] = useState<{ label: string; value: string; }[]>([]);
    const [selectedDateFilter, setSelectedDateFilter] = useState<string>('today');

    const [filters, setFilters] = useState<GetCamerasParameters>({
        types: [],
        installationCompanyId: undefined,
        orderBy: 'createdAt',
        sortOrder: 'DESC',
        beginIn: moment().startOf('day').toDate().getTime(),
        endIn: moment().endOf('day').toDate().getTime(),
        hideChildren: false,
    });

    async function getCamerasList(filters: GetCamerasParameters) {
        try {
            setIsLoading(true);
            const cameras = await cameraService.getCameras(filters);
            setCameras(cameras);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function setPickers() {
        try {
            const res = await accessService.getInstallationCompaniesSimplified();
            let companies = [];
            companies.push({ label: translate('all'), value: 0 });
            companies = companies.concat(res.filter((company) => !company.deletedAt).map((company) => {
                return {
                    value: company.id,
                    label: company.name
                };
            }));
            setSelectableInstallationCompanyFilter(companies);
            setSelectableCamerasTypeFilter([
                { label: translate('all'), value: 'all' },
                { label: translate('ptz'), value: 'ptz' },
                { label: translate('lpr'), value: 'lpr' },
                { label: translate('pinned'), value: 'pinned' },
                { label: translate('panoramic'), value: 'panoramic' }
            ]);
            setSelectableDateFilter([
                { label: translate('today'), value: 'today' },
                { label: translate('last7Days'), value: 'last7Days' },
                { label: translate('last15Days'), value: 'last15Days' },
                { label: translate('last30Days'), value: 'last30Days' },
                { label: translate('last60Days'), value: 'last60Days' },
                { label: translate('last90Days'), value: 'last90Days' },
            ]);
        } catch (err) {
            console.error(err);
        }
    }

    async function getCamerasSummary() {
        try {
            const camerasSummary = await dashboardSummaryService.getCamerasSummary(filters.beginIn || moment().toDate().getTime());
            setSummary(camerasSummary);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        setPickers();
    }, []);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getCamerasList(filters);
            getCamerasSummary();
        }
    }, [isFocused, filters]));

    return (
        <Container style={{ gap: 16 }}>
            <Header
                title={translate('installedCameras')}
            />
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 20 }}>
                <View style={{ flex: 2, flexDirection: 'row', justifyContent: 'space-between', minWidth: 250 }}>
                    <View style={styles.columns}>
                        <View style={styles.rows}>
                            <MyAppText style={styles.number}>{summary.createdToday} </MyAppText>
                            <MyAppText style={styles.text}>{translate(selectedDateFilter)} </MyAppText>
                        </View>
                        <View style={styles.rows}>
                            <MyAppText style={styles.number}>{summary.online} </MyAppText>
                            <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                <MyAppText style={styles.text}>{translate('online')} </MyAppText>
                                <View style={[styles.coloredDot, { backgroundColor: '#00BA39' }]}></View>
                            </View>
                        </View>
                    </View>
                    <View style={styles.columns}>
                        <View style={styles.rows}>
                            <MyAppText style={styles.number}>{summary.online + summary.offline} </MyAppText>
                            <MyAppText style={styles.text}>{translate('total')} </MyAppText>
                        </View>
                        <View style={styles.rows}>
                            <MyAppText style={styles.number}>{summary.offline} </MyAppText>
                            <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                <MyAppText style={styles.text}>{translate('offline')} </MyAppText>
                                <View style={[styles.coloredDot, { backgroundColor: '#FF0027' }]}></View>
                            </View>
                        </View>
                    </View>
                    <View style={styles.columns}>
                        <View style={styles.rows}>
                            <MyAppText style={styles.number}>{summary.groupedCoordinatedCount} </MyAppText>
                            <MyAppText style={styles.text}>{translate('uniqueInstallationLocations')} </MyAppText>
                        </View>
                        <View style={styles.rows}></View>
                    </View>
                </View>
                {!windowInfo.isMobile ?
                    <Card style={{ flexGrow: 3 }}>
                        <View style={styles.cameraTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('type')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('amount')}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.text}>{translate('online')} </MyAppText>
                                    <View style={[styles.coloredDot, { backgroundColor: '#00BA39' }]}></View>
                                </View>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.text}>{translate('offline')} </MyAppText>
                                    <View style={[styles.coloredDot, { backgroundColor: '#FF0027' }]}></View>
                                </View>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.cameraTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('pinned')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.pinnedOffline + summary.pinnedOnline}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.pinnedOnline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.pinnedOnline / (summary.pinnedOffline + summary.pinnedOnline > 0 ? summary.pinnedOffline + summary.pinnedOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.pinnedOffline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.pinnedOffline / (summary.pinnedOffline + summary.pinnedOnline > 0 ? summary.pinnedOffline + summary.pinnedOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.cameraTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('lpr')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.lprOffline + summary.lprOnline}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.lprOnline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.lprOnline / (summary.lprOffline + summary.lprOnline > 0 ? summary.lprOffline + summary.lprOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.lprOffline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.lprOffline / (summary.lprOffline + summary.lprOnline > 0 ? summary.lprOffline + summary.lprOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.cameraTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('ptz')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.ptzOffline + summary.ptzOnline}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.ptzOnline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.ptzOnline / (summary.ptzOffline + summary.ptzOnline > 0 ? summary.ptzOffline + summary.ptzOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.ptzOffline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.ptzOffline / (summary.ptzOffline + summary.ptzOnline > 0 ? summary.ptzOffline + summary.ptzOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.cameraTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('panoramic')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.panoramicOffline + summary.panoramicOnline}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.panoramicOnline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.panoramicOnline / (summary.panoramicOffline + summary.panoramicOnline > 0 ? summary.panoramicOffline + summary.panoramicOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{summary.panoramicOffline}</MyAppText>
                                    <MyAppText style={styles.tablePercentage}>({Math.round((summary.panoramicOffline / (summary.panoramicOffline + summary.panoramicOnline > 0 ? summary.panoramicOffline + summary.panoramicOnline : 1)) * 100)})%</MyAppText>
                                </View>
                            </View>
                        </View>
                        <View style={styles.tableLine}></View>
                        <View style={styles.cameraTableRow}>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{translate('stolen_equipment')} </MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <MyAppText style={styles.tableText}>{summary.stolen}</MyAppText>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{ }</MyAppText>

                                </View>
                            </View>
                            <View style={styles.tableRows}>
                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                    <MyAppText style={styles.tableText}>{ }</MyAppText>
                                </View>
                            </View>
                        </View>
                    </Card> : undefined}
            </View>
            <Card>
                <DataTableHeader
                    title={translate('lastInstalledCameras')}
                    zIndex={1}
                    filter={<View style={{ flexDirection: 'row', gap: 8 }}>
                        <View style={{ maxWidth: 230, minWidth: 230, zIndex: 4, rowGap: 5 }}>
                            <MyAppText style={styles.text}>{translate('period')}</MyAppText>
                            <MyDropDownPicker
                                open={isDatePickerOpen}
                                value={selectedDateFilter}
                                items={selectableDateFilter}
                                setOpen={setIsDatePickerOpen}
                                setValue={(value) => {
                                    const selected = value(selectedDateFilter);
                                    setSelectedDateFilter(selected);

                                    let start, finish;
                                    if (selected === 'last7Days') {
                                        start = moment().subtract(7, 'days').startOf('day');
                                        finish = moment().endOf('day');
                                    } else if (selected === 'last15Days') {
                                        start = moment().subtract(15, 'days').startOf('day');
                                        finish = moment().endOf('day');
                                    } else if (selected === 'last30Days') {
                                        start = moment().subtract(30, 'days').startOf('day');
                                        finish = moment().endOf('day');
                                    } else if (selected === 'last60Days') {
                                        start = moment().subtract(60, 'days').startOf('day');
                                        finish = moment().endOf('day');
                                    } else if (selected === 'last90Days') {
                                        start = moment().subtract(90, 'days').startOf('day');
                                        finish = moment().endOf('day');
                                    } else {
                                        start = moment().startOf('day');
                                        finish = moment().endOf('day');
                                    }
                                    setFilters({ ...filters, beginIn: start.toDate().getTime(), endIn: finish.toDate().getTime() });
                                }}
                                height={32}
                                searchable={true}
                                borderColor={theme.colors.tabs.border}
                            />
                        </View>
                        <View style={{ maxWidth: 230, minWidth: 230, zIndex: 3, rowGap: 5 }}>
                            <MyAppText style={styles.text}>{translate('installationCompany')}</MyAppText>
                            <MyDropDownPicker
                                open={isInstallationCompanyPickerOpen}
                                value={selectedInstallationCompanyFilter}
                                items={selectableInstallationCompanyFilter}
                                setOpen={setIsInstallationCompanyPickerOpen}
                                setValue={(value) => {
                                    setSelectedInstallationCompanyFilter(value);
                                    const selected = value(selectedInstallationCompanyFilter);
                                    if (selected !== 'all') {
                                        setFilters({ ...filters, installationCompanyId: selected });
                                    } else {
                                        setFilters({ ...filters, installationCompanyId: undefined });
                                    }
                                }}
                                height={32}
                                searchable={true}
                                borderColor={theme.colors.tabs.border}
                            />
                        </View>
                        <View style={{ maxWidth: 230, minWidth: 230, zIndex: 2, rowGap: 5 }}>
                            <MyAppText style={styles.text}>{translate('cameraType')}</MyAppText>
                            <MyDropDownPicker
                                open={isCameraTypePickerOpen}
                                value={selectedCameraTypeFilter}
                                items={selectableCamerasTypeFilter}
                                setOpen={setIsCameraTypePickerOpen}
                                setValue={(value) => {
                                    setSelectedCameraTypeFilter(value);
                                    const selected = value(selectedCameraTypeFilter);
                                    if (selected !== 'all') {
                                        setFilters({ ...filters, types: [selected] });
                                    } else {
                                        setFilters({ ...filters, types: [] });
                                    }
                                }}
                                height={32}
                                borderColor={theme.colors.tabs.border}
                            />
                        </View>
                    </View>}
                />
                <DataTableContainer
                    dataTable={
                        <MyDataTable
                            onRowClicked={(row) => {
                                openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: row.id } });
                            }}
                            columns={[{
                                name: translate('serialNo'),
                                selector: row => row.serialNo || '',
                                wrap: true
                            },
                            {
                                name: translate('title'),
                                selector: row => row.title,
                                wrap: true
                            },
                            {
                                name: translate('address'),
                                selector: row => row.address,
                                wrap: true,
                            },
                            {
                                name: translate('installationCompany'),
                                selector: row => row.installationCompany?.name || '',
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                cell: row =>
                                    <MyAppText style={[row.disabledAt ? { color: theme.colors.neutral[200] } : row.isConnected ? {} : { color: '#FF0027' }]}>
                                        {row.disabledAt ? translate('disabled') : row.isConnected ? translate('online') : translate('offline')}
                                    </MyAppText>
                            },
                            {
                                name: translate('type'),
                                selector: row => translate(row.type),
                            },
                            {
                                name: translate('registeredAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true
                            },
                            {
                                name: '',
                                wrap: true,
                                button: true,
                                cell: row => {
                                    const actions: Action[] = [{
                                        content: translate('watchCamera'),
                                        onPress: () => {
                                            navigation.navigate('Cameras', {
                                                screen: 'CameraPlayer',
                                                params: { id: row.id }
                                            });
                                        },
                                    }, {
                                        content: translate('watchCameraOnNewTab'),
                                        onPress: () => {
                                            openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: row.id } });
                                        },
                                    }, {
                                        content: authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_management ? translate('editInformation') : translate('seeInformation'),
                                        onPress: () => {
                                            navigation.navigate('Cameras', {
                                                screen: 'CameraDetails',
                                                params: { id: row.id }
                                            });
                                        },
                                    }];

                                    if ((authenticatedUser?.isAdmin || authenticatedUser?.permissions.crm_module_access) && !row.thirdPartyCamera) {
                                        actions.push({
                                            content: translate('openOnCrm'),
                                            onPress: () => {
                                                navigation.navigate('CRM', {
                                                    screen: 'ManagementDetails',
                                                    params: { id: row.id }
                                                });
                                            },
                                        });
                                    }

                                    return <DropdownButton actions={actions} />;
                                }

                            }]}
                            data={cameras}
                            progressPending={isLoading}
                        />
                    }
                />
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    tableLine: {
        borderBottomColor: theme.colors.container.border,
        borderBottomWidth: 1,
    },
    text: {
        fontSize: 15,
        color: theme.colors.labelColor,
        whiteSpace: 'break-spaces'
    },
    tableText: {
        color: theme.colors.labelColor,
        margin: 5,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    tableRows: {
        flex: 1,
        justifyContent: 'space-evenly',
    },
    cameraTableRow: {
        flexDirection: 'row',
        flex: 1
    },
    number: {
        fontSize: 35,
        fontWeight: 'bold'
    },
    coloredDot: {
        width: 8,
        height: 8,
        borderRadius: 50
    },
    rows: {
        flexDirection: 'column',
        flex: 1
    },
    columns: {
        flex: 1,
        flexDirection: 'column'
    },
    tablePercentage: {
        color: theme.colors.labelColor,
        fontSize: 10,
    },


}));
